/**
 * MouseTooltip · 跟随鼠标的浮窗
 *
 * 设计：
 * - 用 React Portal 挂到 document.body（避免被表格 overflow:hidden 截断）
 * - position: fixed，监听 mousemove 实时更新位置
 * - mouseenter / mouseleave 控制显隐（避免快速划过闪烁，加 50ms 关闭延迟）
 * - 自动避让右/下边缘（防止浮窗超出视口）
 * - 视觉与图表 dark tooltip 一致（深色卡片）
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

export interface IMouseTooltipProps {
  /** 浮窗内容（仅在显示时渲染） */
  content: React.ReactNode;
  /** trigger 子元素（鼠标移入它时显示浮窗） */
  children: React.ReactElement;
  /** 浮窗距离鼠标的偏移（默认 12 / 18） */
  offsetX?: number;
  offsetY?: number;
  /** 浮窗宽度（用于边缘避让计算） */
  estimatedWidth?: number;
  /** 浮窗高度（用于边缘避让计算） */
  estimatedHeight?: number;
  /** 自定义浮窗 className（覆盖默认深色样式） */
  className?: string;
}

export default function MouseTooltip({
  content,
  children,
  offsetX = 12,
  offsetY = 18,
  estimatedWidth = 280,
  estimatedHeight = 200,
  className,
}: IMouseTooltipProps) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const updatePos = useCallback(
    (clientX: number, clientY: number) => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let x = clientX + offsetX;
      let y = clientY + offsetY;
      // 避让右边缘：超出则放鼠标左侧
      if (x + estimatedWidth > vw - 8) {
        x = clientX - offsetX - estimatedWidth;
      }
      // 避让下边缘：超出则上移
      if (y + estimatedHeight > vh - 8) {
        y = vh - estimatedHeight - 8;
      }
      if (x < 8) x = 8;
      if (y < 8) y = 8;
      setPos({ x, y });
    },
    [offsetX, offsetY, estimatedWidth, estimatedHeight],
  );

  const handleEnter = useCallback(
    (e: React.MouseEvent) => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      updatePos(e.clientX, e.clientY);
      setVisible(true);
    },
    [updatePos],
  );

  const handleMove = useCallback(
    (e: React.MouseEvent) => {
      updatePos(e.clientX, e.clientY);
    },
    [updatePos],
  );

  const handleLeave = useCallback(() => {
    // 延迟关闭，避免快速划过闪烁
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setVisible(false), 50);
  }, []);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  // 包装原 children：注入鼠标事件
  const trigger = React.cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      handleEnter(e);
      children.props.onMouseEnter?.(e);
    },
    onMouseMove: (e: React.MouseEvent) => {
      handleMove(e);
      children.props.onMouseMove?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      handleLeave();
      children.props.onMouseLeave?.(e);
    },
  });

  return (
    <>
      {trigger}
      {visible &&
        ReactDOM.createPortal(
          <div
            className={`dbom-mouse-tooltip ${className ?? ''}`}
            style={{
              position: 'fixed',
              left: pos.x,
              top: pos.y,
              zIndex: 9999,
              pointerEvents: 'none',
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
